import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { resolveLanding } from '../../lib/onboardingState.js';
import { guideReturnToFromSearch } from './guideReturnTo.js';
import './personaLogin.css';
import './auth.redesign.css';

export function ChangePasswordPage() {
  const { user, auth, defaultLanding, changePassword } = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  const guideReturnTo = guideReturnToFromSearch(location.search);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && !auth?.mustChangePassword) {
      if (guideReturnTo) {
        window.location.assign(guideReturnTo);
        return;
      }
      navigate(resolveLanding(defaultLanding || '/home', user.id), { replace: true });
    }
  }, [auth?.mustChangePassword, defaultLanding, guideReturnTo, navigate, user]);

  async function handleSubmit(event) {
    event.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError(new Error('New password and confirmation must match.'));
      return;
    }

    setBusy(true);
    try {
      await changePassword({ currentPassword, newPassword });
      if (guideReturnTo) {
        window.location.assign(guideReturnTo);
        return;
      }
      navigate(resolveLanding(defaultLanding || '/home', user?.id), { replace: true });
    } catch (nextError) {
      setError(nextError);
      setBusy(false);
    }
  }

  return (
    <div className="persona-login-shell">
      <div className="persona-login-hero">
        <div className="login-brand">
          {/* PRIME is the primary brand (PRIME-BRAND-UX-001); the DHA emblem is
              a secondary affiliation below the headline. */}
          <div className="login-brand-copy">
            <div className="login-kicker">Password activation</div>
            <h1>DHA PRIME</h1>
            <div className="login-acronym">
              Program Resource and Intelligence Management Engine
            </div>
          </div>
          <div className="login-affiliation">
            <span className="login-brand-mark login-brand-seal">
              <img src="/brand/dha-seal.svg" alt="Defense Health Agency seal" />
            </span>
            <span className="login-affiliation-text">Defense Health Agency</span>
          </div>
        </div>
        <p className="login-blurb">
          Your System Admin-issued temporary password needs to be replaced before you enter the
          normal workspace.
        </p>
        <ul className="login-bullets">
          <li>Use the temporary password you just signed in with as the current password</li>
          <li>Your new password must be at least 12 characters long</li>
          <li>Once saved, you will continue into your assigned PRIME landing experience</li>
        </ul>
      </div>

      <div className="persona-login-card">
        <div className="auth-entry-grid auth-entry-grid--single">
          <section className="auth-entry-panel">
            <div className="auth-entry-copy">
              <span className="eyebrow">Required next step</span>
              <h2>Set your permanent password</h2>
              <p className="muted">
                Password resets remain System Admin-managed. This step only completes the required
                first sign-in change.
              </p>
            </div>

            <form className="auth-form-stack" onSubmit={handleSubmit}>
              <label className="form-field">
                <span className="form-label">Current password</span>
                <input
                  className="form-input"
                  type="password"
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                  autoComplete="current-password"
                  autoFocus
                />
              </label>

              <div className="auth-form-split">
                <label className="form-field">
                  <span className="form-label">New password</span>
                  <input
                    className="form-input"
                    type="password"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    autoComplete="new-password"
                  />
                </label>
                <label className="form-field">
                  <span className="form-label">Confirm new password</span>
                  <input
                    className="form-input"
                    type="password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    autoComplete="new-password"
                  />
                </label>
              </div>

              <p className="auth-form-hint">
                Choose a password you can keep after the temporary one is retired.
              </p>

              <button className="btn primary" type="submit" disabled={busy}>
                {busy ? 'Updating password…' : 'Update password'}
              </button>
            </form>

            {error ? <div className="banner red">{error.message}</div> : null}
          </section>
        </div>
      </div>
    </div>
  );
}
